'use client';

import React, { useState, useRef, useEffect } from 'react';
import { Link } from '@/i18n/routing';
import { User, LogOut, ChevronDown } from 'lucide-react';

interface ProfileDropdownProps {
  user: {
    fullname: {
      fname: string;
      lname: string;
    };
    email: string;
  };
  onLogout: () => void;
}

export const ProfileDropdown: React.FC<ProfileDropdownProps> = ({ user, onLogout }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const initials = `${user.fullname.fname?.[0] || ''}${user.fullname.lname?.[0] || ''}`.toUpperCase() || 'U';

  return (
    <div className="relative group/profile cursor-pointer" ref={dropdownRef}>
      {/* Enhanced Outer Glow Ring with Animation */}
      <div className="absolute -inset-3 rounded-2xl bg-gradient-to-r from-brand-primary via-brand-secondary to-brand-primary opacity-0 group-hover/profile:opacity-30 blur-lg animate-pulse transition-all duration-700 ease-out" />
      
      {/* Secondary Glow Ring */}
      <div className="absolute -inset-1 rounded-2xl bg-gradient-to-r from-brand-secondary via-brand-primary to-brand-secondary opacity-0 group-hover/profile:opacity-20 blur-md transition-all duration-500 ease-out" />
      
      {/* Profile Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative flex items-center gap-2 px-3 py-2 rounded-xl bg-gradient-to-br from-[#1a1a2e] via-[#16213e] to-[#0f3460] text-white hover:from-[#1f1f3a] hover:via-[#1a2a4a] hover:to-[#0f4a70] transition-all duration-300 shadow-lg hover:shadow-xl hover:shadow-brand-primary/20 group"
      >
        {/* Avatar */}
        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-brand-primary to-brand-secondary flex items-center justify-center text-white text-sm font-semibold transition-transform duration-300 group-hover:scale-110">
          {initials}
        </div>
        
        {/* Chevron */}
        <ChevronDown 
          className={`w-4 h-4 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`} 
        />
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div className="absolute right-0 rtl:right-auto rtl:left-0 mt-2 w-64 glass-morphism-dark rounded-xl shadow-2xl border border-white/20 overflow-hidden z-[10000] animate-fadeIn">
          {/* User Info Section */}
          <div className="p-4 backdrop-blur-md bg-gradient-to-r from-blue-500/30 to-purple-500/20 shadow-none  text-white">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-brand-primary to-brand-secondary flex items-center justify-center text-white text-lg font-bold">
                {initials}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-white text-sm truncate">
                  {user.fullname.fname} {user.fullname.lname}
                </p>
                <p className="text-xs text-gray-300 truncate">
                  {user.email}
                </p>
              </div>
            </div>
          </div>

          {/* Menu Items */}
          <div className="">
            {/* Logout Button */}
            <button
              onClick={() => {
                setIsOpen(false);
                onLogout();
              }}
              className="w-full px-4 py-3 flex items-center gap-3 text-left text-gray-700 bg-white-80 hover:bg-red-50 hover:text-red-600 transition-colors duration-200 group"
            >
              <LogOut className="w-4 h-4 transition-transform duration-200 group-hover:scale-110" />
              <span className="text-sm font-medium">Logout</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
